import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { navigate } from 'gatsby';
import Text from '@ingka/text';
import { updatePostCodeInContact } from '../reducers/contact/contactReducer';
import { PostalCodeLocation } from '../services/State/model';
import { TemplateProps } from '../types/template';
import { services } from '../context';
import useDirectNavigation from '../hooks/useDirectNavigation';
import {
  getQueryParamsFromURL,
  addPersistingQueryParamsToPath,
  getPath,
} from '../helpers/urlHelper';
import AppFooter from '../components/AppFooter';
import AppHeader from '../components/AppHeader';
import LocationSelector from '../components/LocationSelector';
import LoginLink from '../components/LoginLink';
import PageContent from '../components/PageContent';
import SiteMetadata from '../components/SiteMetadata';
import ZipCode from '../components/ZipCode';
import { mediaQueries } from '../styles/mediaQueries';
import { useStateDispatch, useStateSelector } from '../store';
import { isNativeApp } from '../helpers/nativeAppHelper';
import useAuthentication from '../hooks/useAuthentication';
import useInitPage from '../hooks/useInitPage';
import { clearSelected } from '../reducers/app/appReducer';
import { saveUpdatedSessionItem } from '../helpers/sessionHelper';
import StickyHeader from '../components/StickyHeader';
import { isPlainView } from '../helpers/plainViewHelper';
import StickyFooter from '../components/StickyFooter';
import Button from '@ingka/button';
import { StyledWrapper } from '../components/PageLayout';
import { isZipCodeValid } from '../hooks/useZipCode';
import useTranslation from '../hooks/useTranslation';
import translatePage from '../i18n/translatePage';

/* globals CSS_CONFIG */

interface StyledContainerProps {
  $isPlainView: boolean;
}

const InputsContainer = styled.div<StyledContainerProps>`
  min-height: 6.5rem;
  margin-bottom: 0;
  ${mediaQueries.portraitTabletUp} {
    min-height: 14rem;
    max-width: ${(props: StyledContainerProps) =>
      props.$isPlainView ? '100%' : '320px'};
  }
`;

const { loginRequired } = CSS_CONFIG.SERVICES;
const { apiSrc } = CSS_CONFIG.GETSTATES;
const showLoginLinkButton = CSS_CONFIG.SHOWLOGINLINKBUTTON;

const IndexTemplate: React.FC<TemplateProps> = ({
  pageContext,
}): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useStateDispatch();
  const { operation, view } = pageContext;
  const { isAuthenticating } = useAuthentication();
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const { isInitPage } = useInitPage(
    operation,
    isAuthenticating || isLoadingPage,
  );

  const searchPostCode = useStateSelector((state) => state.search.postCode);
  const userPostCode = useStateSelector((state) => state.user.postCode);
  const contactPostCode = useStateSelector((state) => state.contact.postCode);
  const [isValidPostalCode, setIsValidPostalCode] = useState(true);
  const [postalCodeLocations, setPostalCodeLocations] = useState<
    PostalCodeLocation[]
  >([]);
  const [isLoadingPostalCodeLocations, setIsLoadingPostalCodeLocations] =
    useState(false);
  const [{ navigateDirect }] = useDirectNavigation(pageContext);
  const isViewPlain = view === 'plain' || isPlainView();
  const isViewNative = view === 'native' || isNativeApp();
  const loadAsEmptyPage = isInitPage && isViewNative;
  const zipCodeRef = useRef<HTMLInputElement>(null);

  const updatePostalCodeLocations = async (): Promise<boolean> => {
    setIsLoadingPostalCodeLocations(true);
    const postalCodeLookup =
      await services.state.getPostalCodeLocations(searchPostCode);
    setPostalCodeLocations(postalCodeLookup.locations);
    setIsLoadingPostalCodeLocations(false);

    if (!postalCodeLookup.valid) {
      setIsValidPostalCode(false);
      return false;
    }

    return postalCodeLookup.locations.length < 2;
  };

  const shouldUpdatePostalCodeLocations = (): boolean => {
    const hasApiConfigured = apiSrc !== 'NONE';
    const hasUpdatedPostalCodeLocations = postalCodeLocations.length;
    return hasApiConfigured && !hasUpdatedPostalCodeLocations;
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    let navigateToServices = true;

    if (zipCodeRef?.current && !isZipCodeValid(zipCodeRef.current.value)) {
      setIsValidPostalCode(isZipCodeValid(zipCodeRef.current.value));
      navigateToServices = false;
    }

    if (shouldUpdatePostalCodeLocations()) {
      navigateToServices = await updatePostalCodeLocations();
    }

    if (navigateToServices) {
      if (contactPostCode) {
        dispatch(updatePostCodeInContact({ postCode: searchPostCode }));
      }
      const path = getPath(operation, 'services');
      navigate(addPersistingQueryParamsToPath(path));
    }
  };

  useEffect((): void => {
    const doNavigateDirect = async (): Promise<void> => {
      const { zip, service, refApp } = getQueryParamsFromURL();
      if (refApp) {
        saveUpdatedSessionItem('refApp', refApp);
      }
      if (zip) {
        await navigateDirect({
          onDirectNavigationFailed: () => {
            dispatch(clearSelected());
            setIsLoadingPage(false);
          },
          zip: decodeURI(zip.toUpperCase()),
          service: decodeURI(service),
        });
      } else {
        dispatch(clearSelected());
        setIsLoadingPage(false);
      }
    };

    doNavigateDirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect((): void => {
    if (postalCodeLocations.length) setPostalCodeLocations([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchPostCode]);

  return (
    <>
      <SiteMetadata pageTitle={t('pages.start.pageTitle')} />
      {!loadAsEmptyPage && (
        <>
          {isViewPlain ? (
            <StickyHeader
              page="start"
              isLoading={isLoadingPage || isInitPage}
            />
          ) : (
            <AppHeader page="start" />
          )}
          <form onSubmit={handleSubmit}>
            <StyledWrapper $withStickyFooter={isViewPlain || isViewNative}>
              <PageContent isLoading={isLoadingPage || isInitPage} view={view}>
                <Text tagName="h2" textStyle="Heading.L">
                  {isViewPlain
                    ? t('pages.start.plainViewTitle')
                    : t('pages.start.title')}
                </Text>
                <Text tagName="p" textStyle="Body.L">
                  {isViewPlain
                    ? t('pages.start.plainViewDescription')
                    : t('pages.start.description')}
                </Text>
                {!isViewNative && !isViewPlain && (
                  <LoginLink
                    isButton={showLoginLinkButton}
                    showLoginPrompt={!!loginRequired.length}
                  />
                )}

                <InputsContainer $isPlainView={isViewPlain}>
                  <ZipCode
                    name="zip"
                    defaultValue={searchPostCode || userPostCode || ''}
                    isValid={isValidPostalCode}
                    setIsValid={setIsValidPostalCode}
                    customRef={zipCodeRef}
                  />
                  <LocationSelector locations={postalCodeLocations} />
                </InputsContainer>
                {isViewPlain || isViewNative ? (
                  <StickyFooter>
                    <Button
                      htmlType="submit"
                      id="nextButton"
                      disabled={!isValidPostalCode || !searchPostCode}
                      loading={isLoadingPostalCodeLocations}
                      text={t('components.stickyFooter.nextButton')}
                      type="emphasised"
                      aria-label={t(
                        'components.stickyFooter.ariaLabelToServices',
                      )}
                      data-cy="next-button"
                    />
                  </StickyFooter>
                ) : (
                  <AppFooter
                    isLoading={isLoadingPostalCodeLocations}
                    nextVisible
                    nextDisabled={!isValidPostalCode || !searchPostCode}
                  />
                )}
              </PageContent>
            </StyledWrapper>
          </form>
        </>
      )}
    </>
  );
};

export default translatePage()(IndexTemplate);
